/**
 * Show Translation Data Layer
 *
 * Admin UI data operations for show translations.
 * Provides CRUD operations for the show_translations table.
 *
 * @module showTranslation
 */

import type { ShowTranslation } from '@musedrops/shared';
import { fromDbKeys, toDbKeys, withUpdateTimestamp, logger } from '@musedrops/shared';
import { supabase } from '../services/supabase';

/**
 * Get all translations for a show
 */
export async function getShowTranslations(showSlug: string): Promise<ShowTranslation[]> {
  const { data, error } = await supabase
    .from('show_translations')
    .select('*')
    .eq('show_slug', showSlug)
    .order('lang', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch translations for show ${showSlug}: ${error.message}`);
  }

  return (data ?? []).map(row => fromDbKeys<ShowTranslation>(row as Record<string, unknown>));
}

/**
 * Get a single show translation by show slug and language
 */
export async function getShowTranslation(
  showSlug: string,
  lang: string
): Promise<ShowTranslation | null> {
  const { data, error } = await supabase
    .from('show_translations')
    .select('*')
    .eq('show_slug', showSlug)
    .eq('lang', lang)
    .maybeSingle(); // Use maybeSingle() instead of single() to gracefully handle no rows

  if (error) {
    throw new Error(`Failed to fetch translation for ${showSlug}/${lang}: ${error.message}`);
  }

  if (!data) {
    return null;
  }

  return fromDbKeys<ShowTranslation>(data as Record<string, unknown>);
}

/**
 * Create a new show translation record
 */
export async function createShowTranslation(
  showSlug: string,
  lang: string,
  data?: Partial<Pick<ShowTranslation, 'name' | 'description' | 'searchTerms'>>
): Promise<ShowTranslation> {
  const insertPayload = toDbKeys({
    showSlug,
    lang,
    ...(data || {}),
  });

  const { data: result, error } = await supabase
    .from('show_translations')
    .insert(insertPayload)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to create translation for ${showSlug}/${lang}: ${error.message}`);
  }

  return fromDbKeys<ShowTranslation>(result as Record<string, unknown>);
}

/**
 * Update an existing show translation record
 */
export async function updateShowTranslation(
  showSlug: string,
  lang: string,
  updates: Partial<Pick<ShowTranslation, 'name' | 'description' | 'searchTerms' | 'audioLogoDesc' | 'enabled' | 'disabledAt' | 'disabledReason'>>
): Promise<ShowTranslation> {
  const updatePayload = withUpdateTimestamp(toDbKeys(updates));

  const { data, error } = await supabase
    .from('show_translations')
    .update(updatePayload)
    .eq('show_slug', showSlug)
    .eq('lang', lang)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to update translation for ${showSlug}/${lang}: ${error.message}`);
  }

  return fromDbKeys<ShowTranslation>(data as Record<string, unknown>);
}

/**
 * Upsert a show translation (create if not exists, update if exists)
 */
export async function upsertShowTranslation(
  showSlug: string,
  lang: string,
  data: Partial<Pick<ShowTranslation, 'name' | 'description' | 'searchTerms' | 'audioLogoDesc'>>
): Promise<ShowTranslation> {
  const upsertPayload = withUpdateTimestamp(toDbKeys({
    showSlug,
    lang,
    ...data,
  }));

  const { data: result, error } = await supabase
    .from('show_translations')
    .upsert(upsertPayload, {
      onConflict: 'show_slug,lang',
    })
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to upsert translation for ${showSlug}/${lang}: ${error.message}`);
  }

  return fromDbKeys<ShowTranslation>(result as Record<string, unknown>);
}

/**
 * Delete a show translation record
 */
export async function deleteShowTranslation(showSlug: string, lang: string): Promise<void> {
  const { error } = await supabase
    .from('show_translations')
    .delete()
    .eq('show_slug', showSlug)
    .eq('lang', lang);

  if (error) {
    throw new Error(`Failed to delete translation for ${showSlug}/${lang}: ${error.message}`);
  }
}

/**
 * Trigger translation generation for a show/language
 * Calls gen-show-translation Edge Function directly to queue generation job
 */
export async function triggerTranslationGeneration(
  showId: string,
  lang: string,
  priority: boolean = true
): Promise<{ jobId: number }> {
  logger.info('Triggering show translation generation', { showId, lang, priority });

  const { data, error } = await supabase.functions.invoke('gen-show-translation', {
    body: {
      showId,
      lang,
      priority,
    },
  });

  if (error) {
    throw new Error(`Failed to trigger translation generation: ${error.message}`);
  }

  if (!data?.success) {
    throw new Error(data?.error || 'Translation generation failed');
  }

  return { jobId: data.result.jobId };
}

/**
 * Trigger announcement generation for a show translation
 * Calls gen-show-announce Edge Function with lang parameter
 */
export async function triggerAnnounceGeneration(
  showId: string,
  lang: string,
  priority: boolean = true
): Promise<{ jobId: number }> {
  logger.info('Triggering show announce generation', { showId, lang, priority });

  const { data, error } = await supabase.functions.invoke('gen-show-announce', {
    body: {
      showId,
      lang,
      priority,
    },
  });

  if (error) {
    throw new Error(`Failed to trigger announce generation: ${error.message}`);
  }

  if (!data?.success) {
    throw new Error(data?.error || 'Announce generation failed');
  }

  return { jobId: data.result.jobId };
}
